import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { useFetcher } from '@remix-run/react'
import React from 'react'
import { z } from 'zod'
import { type action } from '#app/routes/products+/$categorySlug.$productId.$productSlug.like-review.tsx'
import { useIsPending } from '#app/utils/misc.tsx'
import { Icon } from './ui/icon'
import { StatusButton } from './ui/status-button'

export const LikeReviewFormSchema = z.object({
	reviewId: z.string(),
	likeUpdateAction: z.union([z.literal('add'), z.literal('remove')]),
})

type ReactionIconProps = {
	reviewId: string
	clickable?: boolean
	likeCount?: number
	isLiked: boolean
}

export const LikeReviewForm: React.FC<ReactionIconProps> = ({
	clickable = false,
	likeCount = 0,
	reviewId,
	isLiked,
}) => {
	const fetcher = useFetcher<typeof action>()
	const isPending = useIsPending()
	const likeUpdateAction = isLiked ? 'remove' : 'add'
	const iconClass = `${
		likeUpdateAction === 'remove'
			? 'text-green-500 stroke-green-700 scale-110'
			: ''
	} ${clickable ? 'cursor-pointer hover:scale-105' : ''}`
	const [form, fields] = useForm({
		id: `like-review-form-${reviewId}`,
		constraint: getZodConstraint(LikeReviewFormSchema),
		lastResult: fetcher.data?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: LikeReviewFormSchema })
		},
		shouldRevalidate: 'onBlur',
	})

	return (
		<fetcher.Form
			method="POST"
			action="like-review"
			preventScrollReset={true}
			{...getFormProps(form)}
		>
			<input
				{...getInputProps(fields.reviewId, { type: 'hidden' })}
				value={reviewId}
			/>
			<input
				{...getInputProps(fields.likeUpdateAction, { type: 'hidden' })}
				value={likeUpdateAction}
			/>
			<StatusButton
				variant={'ghost'}
				type="submit"
				disabled={!clickable}
				status={isPending ? 'pending' : (form.status ?? 'idle')}
				className="hover:bg-primary-100"
			>
				<Icon name={'like'} className={iconClass}>
					<span>{likeCount}</span>
				</Icon>
			</StatusButton>
		</fetcher.Form>
	)
}
